import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Container from '@amzn/awsui-components-core/container';
import ContentLayout from '@amzn/awsui-components-core/content-layout';
import FormField from '@amzn/awsui-components-core/form-field';
import Header from '@amzn/awsui-components-core/header';
import Input from '@amzn/awsui-components-core/input';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Spinner from '@amzn/awsui-components-core/spinner';
import Textarea from '@amzn/awsui-components-core/textarea';
import { FormikProvider } from 'formik';
import React from 'react';
import ColumnsPicker from 'src/components/star-grid/components/ColumnsPicker';
import RowsDimensionPicker from 'src/components/star-grid/components/RowsDimensionPicker';
import VariancePairsPicker from 'src/components/star-grid/components/VariancePairsPicker';
import { useCreatePnlReportForm } from 'src/components/star-grid/hooks/useCreatePnlReportForm';
import { PnlSourceMode } from 'src/components/star-grid/starGridTypes';

export interface CreateReportViewProps {
  readonly onCreated: (reportId: number) => void;
}

export const CreateReportView: React.FC<CreateReportViewProps> = ({ onCreated }) => {
  const {
    text,
    config,
    configLoading,
    configError,
    formik,
    values,
    setValue,
    setFieldTouched,
    setRowValue,
    setFilterValue,
    nameError,
    dimensionsError,
    scenarioSelectionsError,
    varianceAvailableScenarios,
    hasValidationErrors,
    isSubmitting,
    submitError,
    allDimensions,
    sourceModeOptions,
    sourceMode,
    setSourceMode,
    measure,
    setMeasure,
    measureOptions
  } = useCreatePnlReportForm(onCreated);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={text('createReportDescription')}>
          {text('createReportTitle')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {configError && (
          <Alert type="error" header={text('createConfigFailed')}>
            {configError}
          </Alert>
        )}

        {configLoading ? (
          <Box textAlign="center" padding="l">
            <Spinner /> {text('createLoadingConfig')}
          </Box>
        ) : (
          <FormikProvider value={formik}>
            <SpaceBetween size="l">
              {/* Report Name + Description */}
              <div data-testid="stargrid-form-section-details">
                <Container header={<Header variant="h2">{text('createReportDetails')}</Header>}>
                  <SpaceBetween size="l">
                    <FormField label={text('createReportName')} description={text('createNameDescription')} errorText={nameError}>
                      <Input
                        value={values.report_name}
                        onChange={({ detail }) => setValue('report_name', detail.value)}
                        onBlur={() => void setFieldTouched('report_name', true)}
                        placeholder={text('createNamePlaceholder')}
                        invalid={!!nameError}
                        data-testid="stargrid-form-name-input"
                      />
                    </FormField>
                    <FormField
                      label={
                        <span>
                          {text('createDescription')} <i>{text('createOptional')}</i>
                        </span>
                      }
                      description={text('createDescriptionHelp')}
                    >
                      <Textarea
                        value={values.report_description}
                        onChange={({ detail }) => setValue('report_description', detail.value)}
                        onBlur={() => void setFieldTouched('report_description', true)}
                        placeholder={text('createDescriptionPlaceholder')}
                        rows={2}
                        data-testid="stargrid-form-description-input"
                      />
                    </FormField>
                    <FormField label={text('createFactSource')} description={text('createSourceDescription')}>
                      <Select
                        selectedOption={sourceModeOptions.find((o) => o.value === sourceMode) ?? sourceModeOptions[0]}
                        options={sourceModeOptions}
                        onChange={({ detail }) => setSourceMode((detail.selectedOption?.value as PnlSourceMode) ?? 'INGESTED')}
                        data-testid="stargrid-form-source-mode-select"
                      />
                    </FormField>
                  </SpaceBetween>
                </Container>
              </div>

              {/* Rows (row hierarchy) */}
              <div data-testid="stargrid-form-section-rows">
                <Container
                  header={
                    <Header variant="h2" description={text('createRowsDescription')}>
                      {text('createRows')}
                    </Header>
                  }
                >
                  {/* `stretch` so the two-pane picker spans the container, matching the Filters
                      section (an unstretched FormField constrains its control's width). */}
                  <FormField errorText={dimensionsError} stretch>
                    {/* Each selected chip carries its rollup hierarchy and shows the leaf-value
                        dropdown inline (xPT shape). Dimensions come from dimension_registry.json
                        via GET /pnl-hierarchies. */}
                    <RowsDimensionPicker
                      allDimensions={allDimensions}
                      selectedDimensions={values.selected_dimensions}
                      onChange={(selected) => setValue('selected_dimensions', selected)}
                      isReadOnly={false}
                      values={values.row_values}
                      onChangeValue={setRowValue}
                      testIdPrefix="stargrid-form-rows"
                    />
                  </FormField>
                </Container>
              </div>

              {/* Filters (report-time slicing) */}
              <div data-testid="stargrid-form-section-filters">
                <Container
                  header={
                    <Header variant="h2" description={text('createFiltersDescription')}>
                      {text('createFilters')}
                    </Header>
                  }
                >
                  <RowsDimensionPicker
                    allDimensions={allDimensions}
                    selectedDimensions={values.selected_filters}
                    onChange={(selected) => setValue('selected_filters', selected)}
                    isReadOnly={false}
                    selectedLabel={text('createFilters')}
                    selectedDescription={text('createFilterSelectedHelp')}
                    emptySelectedText={text('createFilterEmpty')}
                    availableDescription={text('createFilterAvailableHelp')}
                    values={values.filter_values}
                    onChangeValue={setFilterValue}
                    testIdPrefix="stargrid-form-filters"
                  />
                </Container>
              </div>

              {/* Columns (scenarios × months) */}
              <div data-testid="stargrid-form-section-columns">
                <Container
                  header={
                    <Header variant="h2" description={text('createColumnsDescription')}>
                      {text('createColumns')}
                    </Header>
                  }
                >
                  <FormField errorText={scenarioSelectionsError}>
                    <ColumnsPicker
                      availableScenarios={config?.available_scenarios ?? []}
                      columns={values.scenario_selections}
                      onChange={(next) => setValue('scenario_selections', next)}
                      isReadOnly={false}
                    />
                  </FormField>
                </Container>
              </div>

              {/* Variance Columns (optional) */}
              <div data-testid="stargrid-form-section-variance">
                <Container
                  header={
                    <Header variant="h2" description={text('createVarianceDescription')}>
                      {text('createVarianceColumns')}
                    </Header>
                  }
                >
                  <VariancePairsPicker
                    availableScenarios={varianceAvailableScenarios}
                    pairs={values.variance_pairs}
                    onChange={(next) => setValue('variance_pairs', [...next])}
                  />
                </Container>
              </div>

              {/* Measure (StarGrid-specific: the value column the cube aggregates) */}
              <div data-testid="stargrid-form-section-measure">
                <Container header={<Header variant="h2">{text('createMeasure')}</Header>}>
                  <FormField label={text('createMeasure')} description={text('createMeasureDescription')}>
                    <Select
                      selectedOption={{ label: measure, value: measure }}
                      onChange={({ detail }) => setMeasure(detail.selectedOption.value as string)}
                      options={measureOptions}
                      data-testid="stargrid-form-measure-select"
                    />
                  </FormField>
                </Container>
              </div>

              {hasValidationErrors && (
                <Alert type="error" header={text('createFixFields')}>
                  {text('createRequiredFieldsHelp')}
                </Alert>
              )}

              {submitError && (
                <Alert type="error" header={text('createFailed')}>
                  {submitError}
                </Alert>
              )}

              <Box>
                <Button
                  variant="primary"
                  formAction="none"
                  loading={isSubmitting}
                  onClick={() => void formik.submitForm()}
                  data-testid="stargrid-form-submit"
                >
                  {text('createReportTitle')}
                </Button>
              </Box>
            </SpaceBetween>
          </FormikProvider>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};

export default CreateReportView;
